import React from "react";

import { fetchInternalCapabilities } from "../api/capabilitiesClient";

type InternalCapabilitiesState = {
  internalAdminEnabled: boolean;
  isLoading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
};

const InternalCapabilitiesContext = React.createContext<InternalCapabilitiesState | null>(null);

export function InternalCapabilitiesProvider({ children }: { children: React.ReactNode }): JSX.Element {
  const [internalAdminEnabled, setInternalAdminEnabled] = React.useState(false);
  const [isLoading, setIsLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);

  const refresh = React.useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const capabilities = await fetchInternalCapabilities();
      setInternalAdminEnabled(Boolean(capabilities.internalAdminEnabled));
    } catch (err) {
      setInternalAdminEnabled(false);
      setError(err instanceof Error ? err.message : "Failed to load internal capabilities");
    } finally {
      setIsLoading(false);
    }
  }, []);

  React.useEffect(() => {
    void refresh();
  }, [refresh]);

  return (
    <InternalCapabilitiesContext.Provider
      value={{ internalAdminEnabled, isLoading, error, refresh }}
    >
      {children}
    </InternalCapabilitiesContext.Provider>
  );
}

export function useInternalCapabilities(): InternalCapabilitiesState {
  const context = React.useContext(InternalCapabilitiesContext);
  if (!context) {
    throw new Error("useInternalCapabilities must be used within InternalCapabilitiesProvider");
  }
  return context;
}
